import React, { useState } from 'react';
import AceEditor from 'react-ace';
import { Tabs, Button, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useTheme } from '../App';
import IssueReporterButton from './IssueReporterButton';
import IssueReporterModal from './IssueReporterModal';

import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-textmate';

const { TabPane } = Tabs;

const ApiResponse = ({ response, expanded, selectedApi, requestParams, isProductionMode }) => {
  const [activeTab, setActiveTab] = useState('pretty');
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const { theme, currentTheme } = useTheme();

  const handleCopyResponse = () => {
    const responseText = response ? JSON.stringify(response, null, 2) : '';
    navigator.clipboard.writeText(responseText);
    message.success('Response copied to clipboard');
  };

  const getEditorValue = () => {
    if (!response) {
      return '';
    }
    return activeTab === 'pretty'
      ? JSON.stringify(response, null, 2)
      : JSON.stringify(response);
  };

  const getResponseSize = () => {
    if (!response) {
      return '0 bytes';
    }
    const responseText = JSON.stringify(response);
    const sizeInBytes = new Blob([responseText]).size;

    if (sizeInBytes < 1024) return `${sizeInBytes} bytes`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <>
      <div style={{ height: '100%', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <Tabs
            defaultActiveKey="pretty"
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
          >
            <TabPane tab="Pretty" key="pretty" />
            <TabPane tab="Raw" key="raw" />
          </Tabs>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '10px', color: theme.textSecondary }}>
              Response Size: {getResponseSize()}
            </span>
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={handleCopyResponse}
              style={{ color: theme.primary }}
            >
              Copy
            </Button>
          </div>
        </div>
        
        <div style={{ 
          borderRadius: '6px',
          overflow: 'hidden',
          border: `1px solid ${theme.border}`,
          boxShadow: currentTheme === 'dark' ? '0 2px 6px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <AceEditor
            mode="json"
            theme={currentTheme === 'dark' ? 'monokai' : 'textmate'}
            value={getEditorValue()}
            readOnly={true}
            height={expanded ? '560px' : '280px'}
            width="100%"
            setOptions={{ 
              useWorker: false, 
              wrap: true, 
              printMargin: false,
              showGutter: true,
              highlightActiveLine: false,
              showPrintMargin: false,
              fontSize: 14,
            }}
            style={{ 
              fontFamily: "'Fira Code', 'Source Code Pro', monospace",
            }}
            editorProps={{ $blockScrolling: true }}
          />
        </div>
        
        {/* Add the Report Issue button - only show in dev mode and when there's a response */}
        {response && !isProductionMode && (
          <IssueReporterButton
            onClick={() => setIssueModalVisible(true)}
            theme={theme}
          />
        )}
      </div>
      
      {/* Issue Reporter Modal */}
      <IssueReporterModal
        visible={issueModalVisible}
        onClose={() => setIssueModalVisible(false)}
        apiDetails={selectedApi}
        capturedData={{
          request: {
            url: selectedApi?.route || '',
            method: selectedApi?.method || 'GET',
            headers: {
              'Api-Key': localStorage.getItem('apiKey') || '',
              'Authorization': localStorage.getItem('bearerToken') ? `Bearer ${localStorage.getItem('bearerToken')}` : '',
              'Content-Type': 'application/json'
            },
            body: requestParams || {},
            params: requestParams?.path || {},
            query: requestParams?.query || {}
          },
          response: {
            status: 200, // Always use 200 for successful responses from your API
            headers: {},
            body: response,
            responseTime: 0 // You can pass actual response time from parent if tracking it
          },
          timestamp: new Date()
        }}
        theme={theme}
        currentTheme={currentTheme}
        reporter={{
          userId: localStorage.getItem('memberId') || 'guest',
          name: localStorage.getItem('memberName') || 'Guest User',
          email: localStorage.getItem('memberEmail') || ''
        }}
      />
    </>
  );
};

export default ApiResponse;